import { Component, Input, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { countryCodes } from '../../../modules/admin/user-management/country_code';
import { FeUtil } from '../../../hezky-routes';
export interface FormField {
  name: string;
  label: string;
  type: 'text' | 'email' | 'contact' | 'select' | 'select-group' | 'multiselect' | 'checkbox' | 'file';
  fileLimit?: number;
  subLabel?: string;
  options?: Array<{ nzValue: any; nzLabel: string, nzDisabled?: boolean }>;
  groupedOptions?: { [groupLabel: string]: Array<{ nzValue: any; nzLabel: string; nzDisabled?: boolean }> };
  placeholder?: string;
  validators?: { type: string; value?: number, message: string }[];
  customAttributes?: { class: string;[key: string]: any };
  onChangeMethod?: (value: any) => void;
}

export interface FormConfig {
  formName: string;
  formType: 'create' | 'edit';
  fields: FormField[];
  data?: any;
  submitMethod: (data: any) => void;
  lookupData?: {
    locations?: Array<{ nzValue: any; nzLabel: string }>;
    roles?: Array<{ nzValue: any; nzLabel: string }>;
    applications?: Array<{ nzValue: any; nzLabel: string }>;
  };
}

@Component({
  selector: 'app-hezky-form',
  templateUrl: './hezky-form.component.html',
  styleUrl: './hezky-form.component.scss'
})
export class HezkyFormComponent implements OnInit {
  @Input() config!: FormConfig;
  formGroup!: FormGroup;
  multiRecordList: any[] = [];
  isMultirecord = false;
  isEditing = false;
  editIndex: number | null = null;
  countryCodes$: any[] = [];
  isGatewayForm: boolean = false;
  isSensorForm: boolean = false;
  fileLists: { [key: string]: any[] } = {};

  constructor(protected feUtil: FeUtil) { }

  ngOnInit(): void {
    this.isGatewayForm = ['Edit Gateway', 'Add Gateway'].includes(this.config.formName);
    this.isSensorForm = ['Edit Sensor', 'Add Sensor'].includes(this.config.formName);
    this.initializeForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'].currentValue && !changes['config'].firstChange) {
      this.initializeForm();
    }
  }

  private initializeForm() {
    if (!this.config) return;
    this.createForm();
    if (this.config.formType === 'edit' && this.config.data) {
      setTimeout(() => {
        this.formGroup.patchValue(this.config.data);
      });
    }
  }

  createForm() {
    const formControls = this.config.fields.reduce<{ [key: string]: any }>((controls, field) => {
      if (field.type === 'contact') {
        let initialCountryCode = '+91|India';
        if (this.config.data?.countryCode) {
          initialCountryCode = this.config.data.countryCode;
        }
        controls['countryCode'] = [initialCountryCode, Validators.required];
        controls['contact'] = [this.config.data?.contact || '', [Validators.required, Validators.minLength(10)]];
        this.countryCodes$ = countryCodes;
      } else if (field.type === 'checkbox' && this.isGatewayForm) {
        controls[field.name] = [{ value: this.config.data?.[field.name] || true, disabled: true }, Validators.required];
        controls['severity'] = [{ value: this.config.data?.severity || 'High' || true, disabled: true }, Validators.required];
      }
      else if (field.name === 'capabilities') {
        controls[field.name] = [{ value: this.config.data?.[field.name] || [], disabled: true }, Validators.required];
      }
      else if (field.type === 'file') {
        const fileData = this.config.data?.[field.name] || [];
        controls[field.name] = [fileData];
        if (Array.isArray(fileData) && fileData.length > 0) {
          this.fileLists[field.name] = fileData
            .filter((item: any) => typeof item === 'string')
            .map((url: string, index: number) => ({
              uid: `existing-${index}-${Date.now()}`,
              name: url.split('/').pop() || `Document ${index + 1}`,
              status: 'done',
              url: url
            }));
        } else {
          this.fileLists[field.name] = [];
        }
      }
      else {
        const initialValue = this.config.data?.[field.name] ?? '';
        controls[field.name] = [field.type === 'multiselect' ? [] : initialValue, this.getValidators(field.validators)];
      }
      return controls;
    }, {});
    this.formGroup = this.feUtil.fb.group(formControls);
    this.onChange(formControls);
  }

  getValidators(validators: { type: string; message: string }[] | undefined) {
    if (!validators) return [];
    return validators.map((v) => {
      switch (v.type) {
        case 'required':
          return Validators.required;
        case 'minlength':
          return Validators.minLength(3);
        case 'maxlength':
          return Validators.maxLength(100);
        case 'email':
          return Validators.email;
        case 'pattern':
          return Validators.pattern(v.message);
        case 'min':
          return Validators.min(Number(v.message));
        case 'max':
          return Validators.max(Number(v.message));
        default:
          return null;
      }
    }).filter(validator => validator !== null);
  }

  hasValidatorType(field: any, type: string) {
    if (!field.validators) return false;
    return field.validators.some((validator: any) => validator.type === type);
  }

  onChange(formControls: { [key: string]: any; }) {
    this.formGroup = this.feUtil.fb.group(formControls);
    this.config.fields.forEach(field => {
      if (field.onChangeMethod) {
        this.formGroup.get(field.name)?.valueChanges.subscribe(value => {
          field.onChangeMethod!(value);
        });
      }
    });
  }

  onAddAnother() {
    if (this.formGroup.valid) {
      this.isMultirecord = true;
      if (this.isEditing) {
        this.feUtil.message.error('You are currently editing another record.');
        return;
      }
      if (this.isGatewayForm) {
        const serialNo = this.formGroup.controls['serial_no'].value;
        const isDuplicate = this.multiRecordList.some(record => record.serial_no === serialNo);
        if (isDuplicate) {
          this.feUtil.message.error('Serial number is already present in the added list.');
          return;
        }
      }
      if (this.isSensorForm) {
        const deviceeui = this.formGroup.controls['deviceeui'].value;
        const external_sensor = this.formGroup.controls['external_sensor'].value;
        const isSensorDuplicate = this.multiRecordList.some(record => record.deviceeui === deviceeui);
        const isExtSensorDuplicate = this.multiRecordList.some(record => record.external_sensor === external_sensor);
        if (isSensorDuplicate) {
          this.feUtil.message.error('Device EUI is already present in the added list.');
          return;
        }
        if (isExtSensorDuplicate) {
          this.feUtil.message.error('External Sensor is already mapped in the added list.');
          return;
        }
      }
      this.toggleCapabilitiesField(true);
      const formValue = { ...this.formGroup.value };
      if (!formValue.documents) {
        formValue.documents = [];
      }
      
      const newRecord = { ...formValue, id: this.multiRecordList.length + 1 };
      this.multiRecordList.push(newRecord);
      this.resetForm();
      this.toggleCapabilitiesField(false);
    }
  }
  toggleCapabilitiesField(enable: boolean) {
    if (this.isSensorForm) {
      if (this.formGroup) {
        const capabilitiesControl = this.formGroup.get('capabilities');
        if (capabilitiesControl) {
          if (enable) {
            capabilitiesControl.enable();
          } else {
            capabilitiesControl.disable();
          }
        }
      }
    }
  }
  onEdit(record: any, index: number) {
    if (this.isEditing) {
      this.feUtil.message.error('You are already editing another record. Do you want to replace it?', { nzDuration: 3000 });
      return;
    }
    this.isEditing = true;
    this.editIndex = index;
    this.formGroup.patchValue(record);
    if (this.config && this.config.fields) {
      this.config.fields.forEach(field => {
        if (field.type === 'file' && record[field.name]) {
          const fileData = record[field.name];
          if (Array.isArray(fileData) && fileData.length > 0) {
            this.fileLists[field.name] = fileData.map((doc: any, idx: number) => {
              if (typeof doc === 'string') {
                return {
                  uid: `existing-${idx}-${Date.now()}`,
                  name: doc.split('/').pop() || `Document ${idx + 1}`,
                  status: 'done' as const,
                  url: doc
                };
              }
              const { fileObj } = this.extractFile(doc);
              if (fileObj) {
                return {
                  uid: doc.uid || `file-${idx}-${Date.now()}-${Math.random()}`,
                  name: doc.name || fileObj.name,
                  status: 'done' as const,
                  originFileObj: fileObj
                };
              }
              return doc;
            });
          } else {
            this.fileLists[field.name] = [];
          }
        }
      });
    }
  }

  onUpdateRecord() {
    if (this.editIndex !== null && this.formGroup.valid) {
      const updatedRecord = { ...this.formGroup.value, id: this.multiRecordList[this.editIndex].id };
      if (JSON.stringify(this.multiRecordList[this.editIndex]) === JSON.stringify(updatedRecord)) {
        this.feUtil.message.error('No changes detected.', { nzDuration: 3000 });
        return;
      }
      this.toggleCapabilitiesField(true);
      this.multiRecordList[this.editIndex] = updatedRecord;
      this.resetForm();
      this.isEditing = false;
      this.toggleCapabilitiesField(false);
      this.editIndex = null;
    }
  }

  resetForm() {
    if (this.isGatewayForm) {
      this.formGroup.controls['gateway_name'].reset();
      this.formGroup.controls['serial_no'].reset();
    }
    else {
      this.formGroup.reset();
      this.formGroup.controls['countryCode'].setValue(this.config.data?.countryCode || '+91|India');
    }
    if (this.config && this.config.fields) {
      this.config.fields.forEach(field => {
        if (field.type === 'file') {
          this.fileLists[field.name] = [];
        }
      });
    }
  }

  deleteRecord(id: number, index: number) {
    this.multiRecordList.splice(index, 1); // Removes one record at the given index
    if (this.multiRecordList.length === 0) {
      this.isMultirecord = false;
    }
  }

  onSubmitAll() {
    const allRecords = [...this.multiRecordList];
    if (this.formGroup.valid && this.formGroup.touched) {
      const currentFormValue = { ...this.formGroup.value };
      const isDuplicate = allRecords.some(record => {
        if (currentFormValue.email && record.email) {
          return currentFormValue.email === record.email;
        }
        if (this.isGatewayForm && currentFormValue.serial_no && record.serial_no) {
          return currentFormValue.serial_no === record.serial_no;
        }
        if (this.isSensorForm && currentFormValue.deviceeui && record.deviceeui) {
          return currentFormValue.deviceeui === record.deviceeui;
        }
        return false;
      });
      
      if (!isDuplicate) {
        allRecords.push({ ...currentFormValue, id: allRecords.length + 1 });
      }
    }
    
    if (allRecords.length > 0) {
      this.config.submitMethod(allRecords);
      this.multiRecordList = [];
      this.isMultirecord = false;
      this.resetForm();
    }
  }

  onSubmit() {
    this.toggleCapabilitiesField(true);
    if (this.feUtil.validate(this.formGroup)) {
      this.config.submitMethod([this.formGroup.value]);
    }
    this.toggleCapabilitiesField(false);
  }

  getCustomAttributes(attributes: { [key: string]: any } = {}) {
    const customAttrs: { [key: string]: any } = {};
    if (attributes) {
      for (let key in attributes) {
        if (attributes.hasOwnProperty(key)) {
          customAttrs[key] = attributes[key];
        }
      }
    }
    return customAttrs;
  }

  handleFileUpload(file: any, fieldName: string, fileLimit?: number): boolean {
    if (!this.fileLists[fieldName]) {
      this.fileLists[fieldName] = [];
    }
    const fileToAdd = {
      uid: file.uid || `file-${Date.now()}-${Math.random()}`,
      name: file.name,
      status: 'done' as const,
      originFileObj: file
    };
    const fileSizeInMB = file.size / (1024 * 1024);
    const maxFileSizeMB = fileLimit || 5;
    if (fileSizeInMB > maxFileSizeMB) {
      this.feUtil.message.error(`File "${file.name}" is too large (${fileSizeInMB.toFixed(2)}MB). Max allowed size is ${maxFileSizeMB}MB.`);
      return false;
    }
    this.fileLists[fieldName] = [...this.fileLists[fieldName], fileToAdd];
    const currentFiles = this.formGroup.get(fieldName)?.value || [];
    const existingFiles = currentFiles.filter((f: any) => {
      if (typeof f === 'string') return true;
      const { fileObj } = this.extractFile(f);
      if (fileObj) return true;
      return false;
    });
    this.formGroup.get(fieldName)?.setValue([...existingFiles, file]);
    return false;
  }

  removeFile(file: any, fieldName: string): boolean {
    if (this.fileLists[fieldName]) {
      this.fileLists[fieldName] = this.fileLists[fieldName].filter(f => f.uid !== file.uid);
    }
    const currentFiles = this.formGroup.get(fieldName)?.value || [];
    const updatedFiles = currentFiles.filter((f: any) => {
      if (f instanceof File) {
        return f.name !== file.name;
      }
      if (f?.uid) {
        return f.uid !== file.uid;
      }
      if (typeof f === 'string' && file.url) {
        return f !== file.url;
      }
      if (f?.originFileObj) {
        return f.originFileObj.name !== file.name;
      }
      return true;
    });
    this.formGroup.get(fieldName)?.setValue(updatedFiles);
    return true;
  }

  getFileList(fieldName: string): any[] {
    if (!this.fileLists[fieldName]) {
      this.fileLists[fieldName] = [];
    }
    return this.fileLists[fieldName];
  }

  getFileUploadHandler(fieldName: string, fileLimit?: number): (file: any) => boolean {
    return (file: any) => this.handleFileUpload(file, fieldName, fileLimit);
  }

  getFileRemoveHandler(fieldName: string): (file: any) => boolean {
    return (file: any) => this.removeFile(file, fieldName);
  }

  private extractFile(file: any): { fileObj: File | null; fileName: string } {
    let fileObj: File | null = null;
    let fileName: string = 'document';
    
    if (file?.originFileObj instanceof File) {
      fileObj = file.originFileObj;
      fileName = file.name || file.originFileObj.name;
    } else if (file instanceof File) {
      fileObj = file;
      fileName = file.name;
    }
    
    return { fileObj, fileName };
  }

  private openFilePreview(fileObj: File): void {
    const url = URL.createObjectURL(fileObj);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  private downloadFile(fileObj: File, fileName: string): void {
    const url = URL.createObjectURL(fileObj);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  getFilePreviewHandler(): (file: any) => void {
    return (file: any) => {
      try {
        if (file.url && typeof file.url === 'string') {
          window.open(file.url, '_blank', 'noopener,noreferrer');
          return;
        }
        const { fileObj } = this.extractFile(file);
        if (fileObj) {
          this.openFilePreview(fileObj);
        }
      } catch (error) {
        this.feUtil.message.error('Unable to preview file', { nzDuration: 3000 });
      }
    };
  }

  getFileDownloadHandler(): (file: any) => void {
    return (file: any) => {
      try {
        if (file.url && typeof file.url === 'string') {
          const link = document.createElement('a');
          link.href = file.url;
          link.download = file.name || file.url.split('/').pop() || 'document';
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          return;
        }
        const { fileObj, fileName } = this.extractFile(file);
        if (fileObj) {
          this.downloadFile(fileObj, fileName);
        }
      } catch (error) {
        this.feUtil.message.error('Unable to download file', { nzDuration: 3000 });
      }
    };
  }

  getMultiRecordList(): any[] {
    return this.multiRecordList;
  }

  getIsMultirecord(): boolean {
    return this.isMultirecord;
  }
}